import {
  Gender,
  HivAnamnesisForm0581E,
  HivAnamnesisForm0581E_ClinicalSection,
  TriState,
  VitalStatus
} from 'src/api/requests'
import { formatDates } from 'src/utils'

import { isChildOn } from './index'

const HOME_COUNTRY = 'md'

/** Drops the opportunistic diseases (56) of an "Alte" row left without an ICD-10 element. */
export const cleanClinical = (
  clinical: HivAnamnesisForm0581E_ClinicalSection
): HivAnamnesisForm0581E_ClinicalSection => ({
  ...clinical,
  opportunisticDiseases: (clinical.opportunisticDiseases ?? []).filter(
    (entry) => !!entry.icd10Id
  )
})

/**
 * The form as the API expects it, from the form values (dates as dayjs). Answers that do not apply are
 * cleared, so a draft never stores contradictory data:
 * - only the transmission section of the patient's age group (D.1 from 15 years, D.2 under 15);
 * - no 47.x for men; no death data for a living patient; no departure data unless 13 = Da;
 * - no locality outside Moldova;
 * - no children (48–50) unless the patient is a woman aged 15 or over; no HIV result for a child not tested.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toHivAnamnesisPayload = (values: any): HivAnamnesisForm0581E => {
  const isChild = isChildOn(
    values?.patient?.birthDate,
    values?.institutions?.collectedAt
  )

  const form = formatDates(values ?? {}) as HivAnamnesisForm0581E
  const patient = form.patient ?? {}
  const demographics = patient.demographics ?? {}
  const family = form.family ?? {}

  return {
    ...form,
    patient: {
      ...patient,
      address: {
        ...patient.address,
        cuatmLocalityId:
          patient.address?.country === HOME_COUNTRY
            ? patient.address?.cuatmLocalityId
            : null
      },
      demographics: {
        ...demographics,
        ...(demographics.vitalStatus !== VitalStatus.DECEASED && {
          deathDate: null,
          deathCause: null
        }),
        ...(demographics.leftCountry !== true && {
          leftCountryDate: null,
          leftCountryType: null
        })
      }
    },
    transmissionAdult: isChild ? { heterosexual: {} } : form.transmissionAdult,
    transmissionChild: isChild
      ? form.transmissionChild
      : { motherHeterosexual: {} },
    family: {
      ...family,
      ...(patient.gender === Gender.MALE && {
        gaveBirthBeforeDiagnosis: {},
        currentlyPregnant: {},
        pregnantAfterDiagnosis: {}
      }),
      // 48–50 only for women aged 15 and over.
      children: (patient.gender === Gender.FEMALE && !isChild
        ? (family.children ?? [])
        : []
      ).map((child) => ({
        ...child,
        result: child.testedForHiv === TriState.YES ? child.result : null
      }))
    },
    clinical: cleanClinical(form.clinical ?? {})
  }
}

// A value the system does not have: nothing, an empty list, or false (a flag the system never unsets).
const isEmptyValue = (value: unknown) =>
  value == null ||
  value === false ||
  (Array.isArray(value) && value.length === 0)

type DiseaseEntry = { icd10Id?: string | null }
type ChildEntry = { personId?: string | null }

// 56: the diseases entered by hand and the ones found by the system, one per ICD-10 element.
const mergeDiseases = (stored: unknown, prefilled: unknown) => {
  const byId = new Map<string, DiseaseEntry>()
  for (const entry of [
    ...((stored as DiseaseEntry[] | null) ?? []),
    ...((prefilled as DiseaseEntry[] | null) ?? [])
  ]) {
    if (entry?.icd10Id) byId.set(entry.icd10Id, entry)
  }

  return [...byId.values()]
}

// 48–50: the children entered by hand stay; a child found by the system is added once (by its person record).
const mergeChildren = (stored: unknown, prefilled: unknown) => {
  const current = (stored as ChildEntry[] | null) ?? []
  const known = new Set(current.map((child) => child.personId).filter(Boolean))

  return [
    ...current,
    ...((prefilled as ChildEntry[] | null) ?? []).filter(
      (child) => !child.personId || !known.has(child.personId)
    )
  ]
}

/**
 * A stored section with the values the system has now laid over it ("Preia din sistem", yearly update):
 * what the doctor entered stays unless the system has a value for it.
 */
export const mergeWithSystem = (
  stored: unknown,
  prefilled: unknown
): unknown => {
  if (isEmptyValue(prefilled)) return stored
  if (
    typeof stored !== 'object' ||
    stored === null ||
    Array.isArray(stored) ||
    typeof prefilled !== 'object' ||
    Array.isArray(prefilled)
  ) {
    return prefilled
  }

  const result: Record<string, unknown> = {
    ...(stored as Record<string, unknown>)
  }
  for (const [key, value] of Object.entries(
    prefilled as Record<string, unknown>
  )) {
    result[key] =
      key === 'opportunisticDiseases'
        ? mergeDiseases(result[key], value)
        : key === 'children'
          ? mergeChildren(result[key], value)
          : mergeWithSystem(result[key], value)
  }

  return result
}

/** Section F for the yearly update: the stored values with the system's current ones laid over them. */
export const mergeClinical = mergeWithSystem
